/**
 * Client cards component
 */

import React, { useState, useCallback } from 'react';
import { getTierColor } from '../../services/TierColorService';
import { getYearSpecificData, getYearAndPrevious, getYearSpecificRevenue, getYearSpecificRooms } from '../../utils/yearSpecificUtils';
import { truncateText } from '../../utils/stringUtils';
import { formatCurrency } from '../../services/RevenueCalculationService';
import { calculateConferenceRevenue } from '../../services/conferenceRevenueService';
import { getFilteredSponsors } from '../../services/sponsorDisplayService';
import '../../styles/components/clients/ClientCards.css';
import '../../styles/shared/buttons.css';
import SponsorCarousel from './SponsorCarousel';
import ConferenceSalesCarousel from './ConferenceSalesCarousel';

/**
 * Card-based view for displaying client data
 * @param {Object} props - Component props
 * @param {Array} props.clients - Array of client objects to display
 * @param {Function} props.unpinClient - Function to unpin a client
 * @param {Function} props.isClientLocked - Function to check if client is locked
 * @param {Function} props.toggleClientLock - Function to toggle client lock state
 * @param {Function} props.isCardFlipped - Function to check if card is flipped
 * @param {Function} props.toggleCardFlip - Function to toggle card flip state
 * @param {Function} props.isExternalClient - Function to check if client is external
 * @param {Boolean} props.useUniformColor - Whether to use uniform color scheme
 * @param {Function} props.handleDragStart - Function to handle drag start
 * @param {Function} props.handleDragOver - Function to handle drag over
 * @param {Function} props.handleDragEnd - Function to handle drag end
 * @param {Object} props.draggedItem - Currently dragged item
 * @param {Function} props.setEmailContactInfo - Function to set email contact info
 * @param {Function} props.setShowEmailDialog - Function to show email dialog
 * @param {String} props.selectedYear - The selected year for history mode
 * @returns {JSX.Element} ClientCards component
 */
const ClientCards = ({ 
  clients, 
  unpinClient, 
  isClientLocked, 
  toggleClientLock, 
  isCardFlipped, 
  toggleCardFlip, 
  isExternalClient,
  useUniformColor,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  draggedItem,
  setEmailContactInfo,
  setShowEmailDialog,
  selectedYear,
  sponsorData,
  selectedConference,
  conferenceSalesData
}) => {
  // Track sponsor carousel index for each client
  const [sponsorIndices, setSponsorIndices] = useState({});

  // Create stable callback for sponsor index changes using useCallback
  const handleSponsorIndexChange = useCallback((clientName, newIndex) => {
    setSponsorIndices(prev => ({
      ...prev,
      [clientName]: newIndex
    }));
  }, []); // Empty deps - function is completely stable

  // Uniform color for all cards when toggle is active
  const uniformColor = '#0074D9';

  // Get year and previous year for revenue/rooms display
  const { year, prevYear } = getYearAndPrevious(selectedYear);

  return (
    <div className="card-view">
      {clients.map((client, index) => {
        const clientTier = client['Tier'] || 'Unknown';
        const cardColor = useUniformColor ? uniformColor : getTierColor(clientTier, 'dark');
        const cardLightColor = useUniformColor ? '#f5f8ff' : getTierColor(clientTier, 'light');
        const isExternal = isExternalClient(client);
        const flipped = isCardFlipped(client);
        
        // Get year-specific data for this client
        const yearData = getYearSpecificData(client, selectedYear);
        
        return (
          <div 
            key={index} 
            className={`card-container ${draggedItem?.index === index ? 'dragging' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, client, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
          >
            <div 
              className={`card-flipper ${flipped ? 'flipped' : ''}`}
            >
              {/* --- Front of Card --- */}
              <div className="card-face card-front" style={{ border: `1px solid ${cardColor}40`, borderLeft: `3px solid ${cardColor}` }}>
                <div className="client-card">
                  <div 
                    className="card-header clickable-header"
                    style={{
                      backgroundColor: cardLightColor,
                    }}
                    onClick={() => toggleCardFlip(client)}
                  >
                    <div className="drag-handle" title="Drag to reorder">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                      </svg>
                    </div>
                    <h3 style={{ color: cardColor }}>{client.client_name}</h3>
                    <div className="header-badges">
                      <span className="tier-badge" style={{ backgroundColor: `${cardColor}20`, color: cardColor }}>
                        Tier {clientTier}
                      </span>
                    </div>
                    <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className={`btn--lock card-lock ${isClientLocked(client) ? 'locked' : ''}`}
                        onClick={() => toggleClientLock(client)}
                        title={isClientLocked(client) ? "Unlock client" : "Lock client to top"}
                      >
                        {isClientLocked(client) ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><circle cx="12" cy="16" r="1"></circle><path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><circle cx="12" cy="16" r="1"></circle><path d="M7 11V7a5 5 0 0 1 5.9-4.9"></path>
                          </svg>
                        )}
                      </button>
                      <button 
                        className="btn--unpin card-unpin"
                        onClick={() => unpinClient(client)}
                        title="Unpin client"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill={cardColor} stroke={cardColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path><circle cx="12" cy="10" r="3"></circle>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="card-section">
                      <h4 className="card-section-title" style={{ color: `${cardColor}cc` }}>Group Information</h4>
                      <div className="card-row"><span className="card-label">Country:</span><span className="card-value" style={{ color: cardColor }}>{client.country}</span></div>
<div className="card-row">
  <span className="card-label">Number of Conferences:</span>
  <span
    className="card-value"
    title="The number of Peak Room Nights Client has booked so far in the current year"
    style={{ cursor: "help" }}
  >
    n/a
  </span>
</div>
                      <div className="card-row"><span className="card-label">Manager:</span><span className="card-value">{client.conference_manager}</span></div>
                      <div className="card-row"><span className="card-label">Time Zone:</span><span className="card-value">{client['Time zone'] || 'N/A'}</span></div>
                    </div>
<div className="card-section">
  <h4 className="card-section-title" style={{ color: `${cardColor}cc` }}>Financial Information</h4>
  {(() => {
    // Use conference revenue if active, otherwise use regular year-specific revenue
    if (client.isConferenceRevenueActive) {
      // For conference revenue, show current year only (conference-specific)
      const conferenceRevenue = formatCurrency(client.conferenceRevenue);
      return (
        <>
          <div className="card-row">
            <span className="card-label">Conference Revenue:</span>
            <span className="card-value revenue" style={{ color: cardColor }}>{conferenceRevenue}</span>
          </div>
        </>
      );
    } else {
      const revenueData = getYearSpecificRevenue(client, year, prevYear);
      return (
        <>
          <div className="card-row">
            <span className="card-label">{prevYear} Revenue:</span>
            <span className="card-value revenue" style={{ color: cardColor }}>{revenueData.prevYearRevenue}</span>
          </div>
          <div className="card-row">
            <span className="card-label">{year} Revenue:</span>
            <span className="card-value revenue" style={{ color: cardColor }}>{revenueData.currYearRevenue}</span>
          </div>
        </>
      );
    }
  })()}
</div>
<div className="card-section">
  <h4 className="card-section-title" style={{ color: `${cardColor}cc` }}>Rooms</h4>
  {(() => {
    const roomsData = getYearSpecificRooms(client, year, prevYear);
    return (
      <>
        <div className="card-row">
          <span className="card-label">{prevYear} Rooms:</span>
          <span className="card-value">{roomsData.prevYearRooms}</span>
        </div>
        <div className="card-row">
          <span className="card-label">{year} Rooms:</span>
          <span className="card-value">{roomsData.currYearRooms}</span>
        </div>
      </>
    );
  })()}
</div>
                  </div>
                  {isExternal && (
                    <div className="external-manager-strip">
                      <span className="external-manager-text">Added from {client.conference_manager}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* --- Back of Card --- */}
              <div className="card-face card-back" style={{ border: `1px solid ${cardColor}40`, borderLeft: `3px solid ${cardColor}` }}>
                <div className="client-card">
                  <div 
                    className="card-header clickable-header"
                    style={{
                      backgroundColor: cardLightColor,
                    }}
                    onClick={() => toggleCardFlip(client)}
                  >
                    <h3 style={{ color: cardColor, width: '100%', textAlign: 'center' }}>Back Side - {client.client_name}</h3>
                  </div>
                  <div className="card-content">
                    <div className="card-section">
                      {(() => {
                        // Calculate filtered sponsors based on current selections
                        const filteredSponsorsData = getFilteredSponsors(
                          client.client_name,
                          sponsorData,
                          selectedConference,
                          selectedYear
                        );
                        
                        return (
                          <SponsorCarousel
                            clientName={client.client_name}
                            sponsors={filteredSponsorsData.sponsors}
                            selectedYear={filteredSponsorsData.yearLabel}
                            error={client.sponsorError}
                            onSponsorIndexChange={handleSponsorIndexChange}
                          />
                        );
                      })()}
                    </div>
                    <div className="card-section">
                      <h4 className="card-section-title" style={{ color: `${cardColor}cc` }}>Additional Information</h4>
                      {/* Last Conference (truncated if too long) */}
                      <div className="card-row">
                        <span className="card-label">Last Conference:</span>
                        <span
                          className="card-value"
                          title={client['Segment'] || 'n/a'}
                        >
                          {truncateText(client['Segment']) || 'n/a'}
                        </span>
                      </div>
                      {/* Client Role (hardcoded) */}
                      <div className="card-row">
                        <span className="card-label">Client Role:</span>
                        <span className="card-value">n/a</span>
                      </div>
                    </div>
                    <div className="card-section booking-pattern-section">
                      {(() => {
                        // Get the currently selected sponsor based on the stored index
                        const filteredSponsorsData = getFilteredSponsors(
                          client.client_name,
                          sponsorData,
                          selectedConference,
                          selectedYear
                        );
                        const currentIndex = sponsorIndices[client.client_name] || 0;
                        const currentSponsor = filteredSponsorsData.sponsors[currentIndex];
                        
                        return (
                          <ConferenceSalesCarousel
                            clientName={client.client_name}
                            currentSponsor={currentSponsor}
                            selectedYear={selectedYear}
                            conferenceSalesData={conferenceSalesData}
                            cardColor={cardColor}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ClientCards;
